import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  User, Trophy, Zap, Target, Award, Star,
  Flame, TrendingUp, Calendar, Clock, Package,
  ChevronRight, Sparkles, Crown, CreditCard, AlertCircle, Loader2
} from 'lucide-react';
import useUserStore from '../store/userStore';
import { subscriptionAPI } from '../services/api';
import Achievements from './Achievements';

// Level system configuration
const LEVELS = [
  { level: 1, name: 'Novice', minDP: 0, maxDP: 100 },
  { level: 2, name: 'Apprentice', minDP: 100, maxDP: 250 },
  { level: 3, name: 'Junior Architect', minDP: 250, maxDP: 500 },
  { level: 4, name: 'Architect', minDP: 500, maxDP: 1000 },
  { level: 5, name: 'Senior Architect', minDP: 1000, maxDP: 2000 },
  { level: 6, name: 'Lead Architect', minDP: 2000, maxDP: 4000 },
  { level: 7, name: 'Principal Architect', minDP: 4000, maxDP: 7500 },
  { level: 8, name: 'Staff Architect', minDP: 7500, maxDP: 12500 },
  { level: 9, name: 'Distinguished Architect', minDP: 12500, maxDP: 20000 },
  { level: 10, name: 'Master Architect', minDP: 20000, maxDP: Infinity },
];

const getLevelInfo = (totalDP) => {
  const level = LEVELS.find(l => totalDP >= l.minDP && totalDP < l.maxDP) || LEVELS[LEVELS.length - 1];
  const progress = level.maxDP === Infinity
    ? 100
    : ((totalDP - level.minDP) / (level.maxDP - level.minDP)) * 100;
  return { ...level, progress, currentDP: totalDP };
};

const levelColors = {
  1: 'from-gray-400 to-gray-500',
  2: 'from-green-400 to-green-600',
  3: 'from-blue-400 to-blue-600',
  4: 'from-purple-400 to-purple-600',
  5: 'from-pink-400 to-pink-600',
  6: 'from-orange-400 to-orange-600',
  7: 'from-red-400 to-red-600',
  8: 'from-yellow-400 to-amber-600',
  9: 'from-teal-400 to-cyan-600',
  10: 'from-purple-500 via-pink-500 to-orange-500',
};

const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 hover:shadow-lg transition-all duration-300 group">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-400 mb-1">{label}</p>
        <p className="text-3xl font-bold text-white group-hover:scale-105 transition-transform">
          {value}
        </p>
        {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
      </div>
      <div className={`p-3 rounded-xl bg-gradient-to-br ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const SkillBar = ({ name, score, maxScore = 100 }) => {
  const percentage = (score / maxScore) * 100;
  const getColor = () => {
    if (percentage >= 80) return 'from-green-400 to-emerald-500';
    if (percentage >= 60) return 'from-blue-400 to-blue-500';
    if (percentage >= 40) return 'from-yellow-400 to-orange-500';
    return 'from-red-400 to-red-500';
  };

  return (
    <div className="mb-4">
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-slate-300">{name}</span>
        <span className="text-sm font-bold text-white">{score}</span>
      </div>
      <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${getColor()} transition-all duration-1000 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const ActivityCalendar = ({ activities }) => {
  // Generate last 30 days
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const activity = activities?.[dateStr] || 0;
    days.push({ date: dateStr, activity, day: date.getDate() });
  }

  const getIntensity = (count) => {
    if (count === 0) return 'bg-slate-700';
    if (count === 1) return 'bg-green-200';
    if (count === 2) return 'bg-green-400';
    if (count >= 3) return 'bg-green-600';
    return 'bg-slate-700';
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
      <h3 className="font-bold text-white mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-green-500" />
        Activity (Last 30 Days)
      </h3>
      <div className="flex gap-1 flex-wrap">
        {days.map((day, i) => (
          <div
            key={day.date}
            className={`w-6 h-6 rounded-sm ${getIntensity(day.activity)} transition-colors hover:ring-2 hover:ring-green-400 cursor-pointer`}
            title={`${day.date}: ${day.activity} submissions`}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 mt-4 text-xs text-slate-400">
        <span>Less</span>
        <div className="flex gap-1">
          <div className="w-4 h-4 rounded-sm bg-slate-700" />
          <div className="w-4 h-4 rounded-sm bg-green-200" />
          <div className="w-4 h-4 rounded-sm bg-green-400" />
          <div className="w-4 h-4 rounded-sm bg-green-600" />
        </div>
        <span>More</span>
      </div>
    </div>
  );
};

const SubscriptionCard = ({ user, subscriptionStatus, onRefresh }) => {
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const isPremium = user?.is_premium || subscriptionStatus?.is_premium;
  const expiresAt = user?.premium_expires_at || subscriptionStatus?.expires_at;
  const daysRemaining = expiresAt
    ? Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  const handleCancel = async () => {
    try {
      setCancelling(true);
      await subscriptionAPI.cancelSubscription('User requested cancellation');
      toast.success('Subscription cancelled. Access continues until billing period ends.');
      setShowCancelConfirm(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (!isPremium) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h3 className="font-bold text-white mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-indigo-500" />
          Subscription
        </h3>
        <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
          <p className="text-slate-300 mb-2">You're on the <span className="font-semibold">Free</span> plan</p>
          <p className="text-sm text-slate-400">2 AI evaluations per day, starter challenges only</p>
        </div>
        <Link
          to="/pricing"
          className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
        >
          <Crown className="w-5 h-5" />
          Upgrade to Premium
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
      <h3 className="font-bold mb-4 flex items-center gap-2">
        <Crown className="w-5 h-5 text-yellow-400" />
        Premium Subscription
      </h3>

      <div className="bg-white/10 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-indigo-100">Status</span>
          <span className="px-2 py-0.5 bg-green-400/20 text-green-100 rounded-full text-sm font-medium">
            Active
          </span>
        </div>
        {expiresAt && (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-indigo-100">Expires</span>
              <span className="font-medium">{new Date(expiresAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-indigo-100">Days Remaining</span>
              <span className="font-bold">{daysRemaining > 0 ? daysRemaining : 0}</span>
            </div>
          </>
        )}
      </div>

      <div className="space-y-2 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <span>20 AI evaluations per day</span>
        </div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <span>Access to all challenges</span>
        </div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <span>Priority AI processing</span>
        </div>
      </div>

      {!showCancelConfirm ? (
        <button
          onClick={() => setShowCancelConfirm(true)}
          className="w-full py-2 bg-white/10 text-white/80 text-sm rounded-lg hover:bg-white/20 transition-colors"
        >
          Cancel Subscription
        </button>
      ) : (
        <div className="bg-red-500/20 rounded-lg p-4">
          <p className="text-sm mb-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            Are you sure? Your access will continue until {expiresAt ? new Date(expiresAt).toLocaleDateString() : 'billing period ends'}.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCancelConfirm(false)}
              className="flex-1 py-2 bg-white/20 text-white text-sm rounded-lg hover:bg-white/30"
            >
              Keep Premium
            </button>
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="flex-1 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Yes, Cancel'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const RecentActivity = ({ activities }) => {
  const mockActivities = [
    { type: 'completion', challenge: 'URL Shortener', score: 85, time: '2 hours ago' },
    { type: 'unlock', component: 'Redis Cache', cost: 75, time: '1 day ago' },
    { type: 'achievement', name: 'First Blueprint', reward: 25, time: '1 day ago' },
    { type: 'completion', challenge: 'Rate Limiter', score: 72, time: '3 days ago' },
  ];

  const getIcon = (type) => {
    switch (type) {
      case 'completion': return <Target className="w-5 h-5 text-green-500" />;
      case 'unlock': return <Package className="w-5 h-5 text-blue-500" />;
      case 'achievement': return <Trophy className="w-5 h-5 text-yellow-500" />;
      default: return <Star className="w-5 h-5 text-gray-500" />;
    }
  };

  const getDescription = (activity) => {
    switch (activity.type) {
      case 'completion':
        return `Completed "${activity.challenge}" with score ${activity.score}`;
      case 'unlock':
        return `Unlocked ${activity.component} for ${activity.cost} DP`;
      case 'achievement':
        return `Earned "${activity.name}" +${activity.reward} DP`;
      default:
        return 'Activity';
    }
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
      <h3 className="font-bold text-white mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-purple-500" />
        Recent Activity
      </h3>
      <div className="space-y-4">
        {mockActivities.map((activity, i) => (
          <div key={i} className="flex items-start gap-3 group">
            <div className="p-2 bg-slate-700 rounded-lg group-hover:bg-slate-600 transition-colors">
              {getIcon(activity.type)}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-300">{getDescription(activity)}</p>
              <p className="text-xs text-slate-500">{activity.time}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProfilePage = () => {
  const { user, stats, fetchProfile, subscriptionStatus, fetchSubscriptionStatus } = useUserStore();
  const [activeTab, setActiveTab] = useState('overview');
  const [levelInfo, setLevelInfo] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchSubscriptionStatus();
  }, []);

  useEffect(() => {
    if (user) {
      // Calculate level based on total earned DP (design_points + total_score as proxy)
      const totalDP = (user.design_points || 0) + (user.total_score || 0);
      setLevelInfo(getLevelInfo(totalDP));
    }
  }, [user]);

  if (!user || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Profile Header */}
      <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 rounded-2xl p-8 mb-8 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="relative flex items-center gap-6">
          {/* Avatar */}
          <div className={`relative w-24 h-24 rounded-2xl bg-gradient-to-br ${levelInfo ? levelColors[levelInfo.level] : 'from-gray-400 to-gray-500'} flex items-center justify-center shadow-lg`}>
            <span className="text-4xl font-bold text-white">
              {user.username?.charAt(0).toUpperCase()}
            </span>
            {levelInfo?.level >= 5 && (
              <Crown className="absolute -top-3 -right-3 w-8 h-8 text-yellow-400 drop-shadow-lg animate-bounce" />
            )}
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-3xl font-bold text-white">{user.username}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-bold bg-white/20 text-white`}>
                Level {levelInfo?.level || 1}
              </span>
            </div>

            <p className="text-purple-100 text-lg mb-3">{levelInfo?.name || 'Novice'}</p>

            {/* Level progress bar */}
            <div className="max-w-md">
              <div className="flex justify-between text-sm text-purple-100 mb-1">
                <span>{levelInfo?.currentDP || 0} DP</span>
                <span>{levelInfo?.maxDP === Infinity ? '∞' : levelInfo?.maxDP} DP</span>
              </div>
              <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-1000"
                  style={{ width: `${levelInfo?.progress || 0}%` }}
                />
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-6 h-6 text-yellow-300" />
              <span className="text-3xl font-bold text-white">{user.design_points}</span>
            </div>
            <p className="text-purple-100">Design Points</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-slate-700">
        {['overview', 'achievements', 'stats'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-3 font-medium border-b-2 transition-colors capitalize ${
              activeTab === tab
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-slate-400 hover:text-slate-300'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Trophy}
              label="Challenges Completed"
              value={stats.challenges?.completed || 0}
              subtext={`of ${stats.challenges?.unlocked || 0} unlocked`}
              color="from-yellow-400 to-orange-500"
            />
            <StatCard
              icon={Target}
              label="Total Submissions"
              value={stats.submissions?.total || 0}
              subtext={`Best: ${stats.submissions?.best_score || 0}`}
              color="from-green-400 to-emerald-500"
            />
            <StatCard
              icon={TrendingUp}
              label="Average Score"
              value={Math.round(stats.submissions?.avg_score || 0)}
              subtext="across all submissions"
              color="from-blue-400 to-blue-600"
            />
            <StatCard
              icon={Package}
              label="Components Unlocked"
              value={stats.components?.unlocked || 0}
              subtext={`of ${stats.components?.total || 34} total`}
              color="from-purple-400 to-purple-600"
            />
          </div>

          {/* Skills, Activity & Subscription */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Skill Breakdown
              </h3>
              <SkillBar name="Efficiency" score={Math.round(stats.submissions?.avg_score * 0.4 || 0)} />
              <SkillBar name="Cost Management" score={Math.round(stats.submissions?.avg_score * 0.3 || 0)} />
              <SkillBar name="Reliability" score={Math.round(stats.submissions?.avg_score * 0.3 || 0)} />
              <SkillBar name="Component Usage" score={Math.min((stats.components?.unlocked || 0) * 3, 100)} />
            </div>

            <RecentActivity />

            <SubscriptionCard
              user={user}
              subscriptionStatus={subscriptionStatus}
              onRefresh={fetchSubscriptionStatus}
            />
          </div>

          <ActivityCalendar />
        </div>
      )}

      {activeTab === 'achievements' && <Achievements />}

      {activeTab === 'stats' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="font-bold text-white mb-4">Challenge Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-slate-400">Total Attempted</span>
                <span className="font-bold text-white">{stats.challenges?.attempted || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-slate-400">Completed</span>
                <span className="font-bold text-green-400">{stats.challenges?.completed || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-slate-400">Unlocked</span>
                <span className="font-bold text-blue-400">{stats.challenges?.unlocked || 0}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-400">Completion Rate</span>
                <span className="font-bold text-white">
                  {stats.challenges?.unlocked
                    ? Math.round((stats.challenges.completed / stats.challenges.unlocked) * 100)
                    : 0}%
                </span>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="font-bold text-white mb-4">Submission Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-slate-400">Total Submissions</span>
                <span className="font-bold text-white">{stats.submissions?.total || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-slate-400">Average Score</span>
                <span className="font-bold text-white">{Math.round(stats.submissions?.avg_score || 0)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-slate-400">Best Score</span>
                <span className="font-bold text-green-400">{stats.submissions?.best_score || 0}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-400">Pass Rate (70+)</span>
                <span className="font-bold text-blue-400">
                  {stats.submissions?.total
                    ? Math.round(((stats.submissions?.avg_score || 0) >= 70 ? 1 : 0) * 100)
                    : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
